export enum LayerTier {
  Imagery = 0,
  GroundFixed = 1,
  Data = 2,
  Atmospheric = 3,
  Reference = 4,
}

export type LayerKind =
  | 'overlay'
  | 'terrain-service'
  | 'nwm'
  | 'om'
  | 'emergency'
  | 'wildlife'
  | 'cellular'
  | 'subgroup'
  | 'lightning'

export type BrowseGroup =
  | 'terrain'
  | 'weather'
  | 'imagery'
  | 'hazards'
  | 'land'
  | 'reference'
  | 'emergency'
  | 'wildlife'
  | 'cellular'
  | 'comms'

export interface LayerTierEntry {
  tier: LayerTier
  order: number
  name: string
  kind: LayerKind
  tags: string[]
  browseGroup: BrowseGroup
  children?: string[]
  /**
   * Most-specific region this layer actually has useful data for, shown as a chip under
   * the title. Omitted = worldwide (the unmarked default). Populated from LAYER_REGION below.
   */
  region?: string
  /**
   * Whether the data is model-predicted or measured, shown as a chip under the title.
   * Only set where the distinction is meaningful (weather/observational layers); omitted
   * for static/reference layers. Populated from LAYER_TIMELINESS below.
   */
  timeliness?: 'forecast' | 'observed'
}

const entries: [string, LayerTierEntry][] = [
  // ── Tier 0: Imagery ──────────────────────────────────────────────
  [
    'viirs-snpp-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 10,
      name: 'VIIRS SNPP True Color',
      kind: 'overlay',
      tags: ['viirs', 'snpp', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'viirs-noaa20-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 20,
      name: 'VIIRS NOAA-20 True Color',
      kind: 'overlay',
      tags: ['viirs', 'noaa', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'viirs-noaa21-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 30,
      name: 'VIIRS NOAA-21 True Color',
      kind: 'overlay',
      tags: ['viirs', 'noaa', 'true', 'color', 'imagery', 'satellite'],
      browseGroup: 'imagery',
    },
  ],
  [
    'goes-east-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 40,
      name: 'GOES-East GeoColor',
      kind: 'overlay',
      tags: ['goes', 'east', 'geocolor', 'geostationary', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'goes-west-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 50,
      name: 'GOES-West GeoColor',
      kind: 'overlay',
      tags: ['goes', 'west', 'geocolor', 'geostationary', 'imagery'],
      browseGroup: 'imagery',
    },
  ],
  [
    'hls-sentinel-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 60,
      name: 'Sentinel-2 True Color (HLS)',
      kind: 'overlay',
      tags: ['sentinel', 'hls', 'true', 'color', 'high', 'resolution'],
      browseGroup: 'imagery',
    },
  ],
  [
    'hls-landsat-truecolor',
    {
      tier: LayerTier.Imagery,
      order: 70,
      name: 'Landsat True Color (HLS)',
      kind: 'overlay',
      tags: ['landsat', 'hls', 'true', 'color', 'high', 'resolution'],
      browseGroup: 'imagery',
    },
  ],

  // ── Tier 1: Ground-Fixed ─────────────────────────────────────────
  [
    'hillshade',
    {
      tier: LayerTier.GroundFixed,
      order: 10,
      name: 'Hillshade',
      kind: 'terrain-service',
      tags: ['hillshade', 'shaded', 'relief', 'terrain', 'elevation', 'dem'],
      browseGroup: 'terrain',
    },
  ],
  [
    'slope-angle',
    {
      tier: LayerTier.GroundFixed,
      order: 20,
      name: 'Slope Angle Shading',
      kind: 'terrain-service',
      tags: ['slope', 'angle', 'steepness', 'avalanche'],
      browseGroup: 'terrain',
    },
  ],
  [
    'sun-exposure',
    {
      tier: LayerTier.GroundFixed,
      order: 30,
      name: 'Sun Exposure',
      kind: 'terrain-service',
      tags: ['sun', 'exposure', 'aspect', 'solar', 'shade'],
      browseGroup: 'terrain',
    },
  ],
  [
    'land-cover',
    {
      tier: LayerTier.GroundFixed,
      order: 40,
      name: 'Land Cover',
      kind: 'terrain-service',
      tags: ['land', 'cover', 'vegetation', 'forest', 'urban'],
      browseGroup: 'terrain',
    },
  ],
  [
    'contours',
    {
      tier: LayerTier.GroundFixed,
      order: 50,
      name: 'Contour Lines',
      kind: 'terrain-service',
      tags: ['contour', 'elevation', 'topography'],
      browseGroup: 'terrain',
    },
  ],
  [
    'snow-depth',
    {
      tier: LayerTier.GroundFixed,
      order: 60,
      name: 'Snow Depth — Observed (SNODAS)',
      kind: 'terrain-service',
      tags: ['snow', 'depth', 'snodas', 'snowpack', 'observed'],
      browseGroup: 'hazards',
    },
  ],
  [
    'nwm-snowh',
    {
      tier: LayerTier.GroundFixed,
      order: 70,
      name: 'Snow Depth — Modeled (NWM)',
      kind: 'nwm',
      tags: ['snow', 'depth', 'snowpack', 'modeled', 'nwm'],
      browseGroup: 'hazards',
    },
  ],
  [
    'nwm-qsnow',
    {
      tier: LayerTier.GroundFixed,
      order: 80,
      name: 'Snowfall Rate',
      kind: 'nwm',
      tags: ['snow', 'snowfall', 'rate', 'nwm', 'weather'],
      browseGroup: 'hazards',
    },
  ],
  [
    'avalanche-zones',
    {
      tier: LayerTier.GroundFixed,
      order: 90,
      name: 'Avalanche Zones',
      kind: 'terrain-service',
      tags: ['avalanche', 'avy', 'danger', 'forecast', 'snow', 'backcountry'],
      browseGroup: 'hazards',
    },
  ],
  [
    'viirs-snpp-snow-wms',
    {
      tier: LayerTier.GroundFixed,
      order: 100,
      name: 'Snow Coverage — SNPP',
      kind: 'overlay',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'snpp', 'satellite'],
      browseGroup: 'hazards',
    },
  ],
  [
    'viirs-noaa20-snow-wms',
    {
      tier: LayerTier.GroundFixed,
      order: 110,
      name: 'Snow Coverage — NOAA-20',
      kind: 'overlay',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'noaa', 'satellite'],
      browseGroup: 'hazards',
    },
  ],
  [
    'vegetation-disturbance',
    {
      tier: LayerTier.GroundFixed,
      order: 120,
      name: 'Vegetation Disturbance Alerts',
      kind: 'overlay',
      tags: ['vegetation', 'disturbance', 'burn', 'scar', 'landslide', 'blowdown'],
      browseGroup: 'hazards',
    },
  ],

  // ── Tier 2: Data ─────────────────────────────────────────────────
  [
    'blm-sma',
    {
      tier: LayerTier.Data,
      order: 10,
      name: 'Land Management (BLM SMA)',
      kind: 'terrain-service',
      tags: ['blm', 'land', 'management', 'surface', 'agency', 'ownership', 'public'],
      browseGroup: 'land',
    },
  ],
  [
    'padus',
    {
      tier: LayerTier.Data,
      order: 20,
      name: 'Protected Areas (PAD-US)',
      kind: 'terrain-service',
      tags: [
        'padus',
        'protected',
        'areas',
        'park',
        'wilderness',
        'refuge',
        'access',
        'conservation',
      ],
      browseGroup: 'land',
    },
  ],
  [
    'plss',
    {
      tier: LayerTier.Data,
      order: 30,
      name: 'PLSS Survey Grid',
      kind: 'terrain-service',
      tags: ['plss', 'survey', 'township', 'range', 'section', 'blm', 'land', 'cadastral'],
      browseGroup: 'land',
    },
  ],
  [
    'backcountry',
    {
      tier: LayerTier.Data,
      order: 40,
      name: 'Trails & POIs (OSM)',
      kind: 'terrain-service',
      tags: ['backcountry', 'trail', 'peak', 'shelter', 'campsite', 'water', 'hiking', 'path'],
      browseGroup: 'land',
    },
  ],
  [
    'cotrex',
    {
      tier: LayerTier.Data,
      order: 50,
      name: 'COTREX (Colorado Trails)',
      kind: 'terrain-service',
      tags: ['cotrex', 'colorado', 'trail', 'trailhead', 'hiking', 'cpw'],
      browseGroup: 'land',
    },
  ],
  [
    'cotrex-closures',
    {
      tier: LayerTier.Data,
      order: 60,
      name: 'Wildlife Closures (COTREX)',
      kind: 'terrain-service',
      tags: ['cotrex', 'colorado', 'closure', 'wildlife', 'seasonal', 'restriction'],
      browseGroup: 'land',
    },
  ],
  [
    'climbing-crags',
    {
      tier: LayerTier.Data,
      order: 70,
      name: 'Climbs (OpenBeta)',
      kind: 'terrain-service',
      tags: ['climbing', 'crag', 'rock', 'sport', 'trad', 'bouldering', 'openbeta', 'route'],
      browseGroup: 'land',
    },
  ],
  [
    'fire-wildfires',
    {
      tier: LayerTier.Data,
      order: 80,
      name: 'Active Wildfires (WFIGS)',
      kind: 'terrain-service',
      tags: ['fire', 'wildfire', 'wfigs', 'nifc', 'perimeter', 'incident'],
      browseGroup: 'hazards',
    },
  ],
  [
    'fire-firms',
    {
      tier: LayerTier.Data,
      order: 90,
      name: 'Fire Detections (FIRMS)',
      kind: 'terrain-service',
      tags: ['fire', 'firms', 'hotspot', 'viirs', 'satellite', 'thermal', 'detection'],
      browseGroup: 'hazards',
    },
  ],
  [
    'fire-restrictions',
    {
      tier: LayerTier.Data,
      order: 100,
      name: 'Fire Restrictions',
      kind: 'terrain-service',
      tags: ['fire', 'restriction', 'closure', 'nps', 'usfws', 'park', 'refuge', 'ban'],
      browseGroup: 'hazards',
    },
  ],
  [
    'cellular',
    {
      tier: LayerTier.GroundFixed,
      order: 130,
      name: 'US Cellular Coverage',
      kind: 'cellular',
      tags: ['cellular', 'cell', 'coverage', 'carrier', 'att', 'verizon', 'tmobile', 'signal'],
      browseGroup: 'comms',
    },
  ],
  [
    'satellite',
    {
      tier: LayerTier.Data,
      order: 120,
      name: 'Satellite Positions',
      kind: 'terrain-service',
      tags: [
        'satellite',
        'constellation',
        'iridium',
        'globalstar',
        'starlink',
        'orbit',
        'communication',
      ],
      browseGroup: 'comms',
    },
  ],
  [
    'satellite-los',
    {
      tier: LayerTier.Data,
      order: 130,
      name: 'Satellite Line of Sight',
      kind: 'terrain-service',
      tags: ['satellite', 'line of sight', 'coverage', 'visibility', 'communication', 'signal'],
      browseGroup: 'comms',
    },
  ],
  [
    'emergency-hospitals',
    {
      tier: LayerTier.Data,
      order: 140,
      name: 'Hospitals',
      kind: 'emergency',
      tags: ['hospital', 'trauma', 'emergency', 'medical', 'helipad'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-fire-stations',
    {
      tier: LayerTier.Data,
      order: 150,
      name: 'Fire & EMS Stations',
      kind: 'emergency',
      tags: ['fire', 'ems', 'station', 'emergency'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-law-enforcement',
    {
      tier: LayerTier.Data,
      order: 160,
      name: 'Law Enforcement',
      kind: 'emergency',
      tags: ['sheriff', 'police', 'law', 'enforcement', 'emergency'],
      browseGroup: 'emergency',
    },
  ],
  [
    'emergency-usfs-offices',
    {
      tier: LayerTier.Data,
      order: 170,
      name: 'USFS Ranger Stations',
      kind: 'emergency',
      tags: ['usfs', 'ranger', 'forest', 'service', 'station'],
      browseGroup: 'emergency',
    },
  ],
  [
    'wildlife-inat',
    {
      tier: LayerTier.Data,
      order: 180,
      name: 'Wildlife Sightings',
      kind: 'wildlife',
      tags: ['wildlife', 'inaturalist', 'animal', 'sighting', 'observation'],
      browseGroup: 'wildlife',
    },
  ],
  [
    'wildlife-critical-habitat',
    {
      tier: LayerTier.Data,
      order: 190,
      name: 'Critical Habitat (USFWS)',
      kind: 'wildlife',
      tags: ['critical', 'habitat', 'endangered', 'threatened', 'species', 'usfws'],
      browseGroup: 'wildlife',
    },
  ],
  [
    'wildlife-nps-alerts',
    {
      tier: LayerTier.Data,
      order: 200,
      name: 'Park Alerts (NPS)',
      kind: 'wildlife',
      tags: ['nps', 'park', 'alert', 'closure', 'danger', 'caution'],
      browseGroup: 'land',
    },
  ],

  // ── Tier 2: Data — Subgroups (virtual, for browse/search) ────────
  [
    'subgroup-fire',
    {
      tier: LayerTier.Data,
      order: 80,
      name: 'Fire & Closures',
      kind: 'subgroup',
      tags: ['fire', 'wildfire', 'closure', 'restriction', 'firms', 'wfigs'],
      browseGroup: 'hazards',
      children: ['fire-wildfires', 'fire-firms', 'fire-restrictions', 'om-smoke'],
    },
  ],
  [
    'subgroup-snow-depth',
    {
      tier: LayerTier.GroundFixed,
      order: 60,
      name: 'Snow Depth',
      kind: 'subgroup',
      tags: ['snow', 'depth', 'snowpack', 'snodas', 'nwm', 'observed', 'modeled'],
      browseGroup: 'hazards',
      children: ['snow-depth', 'nwm-snowh'],
    },
  ],
  [
    'subgroup-snow-coverage',
    {
      tier: LayerTier.GroundFixed,
      order: 100,
      name: 'Snow Coverage',
      kind: 'subgroup',
      tags: ['snow', 'cover', 'coverage', 'ndsi', 'viirs', 'snpp', 'noaa', 'satellite'],
      browseGroup: 'hazards',
      children: ['viirs-snpp-snow-wms', 'viirs-noaa20-snow-wms'],
    },
  ],

  // ── Tier 3: Atmospheric ──────────────────────────────────────────
  // NOTE: the NWM Temperature (nwm-t2d), Wind (nwm-wind), Precipitation (nwm-precip),
  // CAPE/Thunderstorm Potential (nwm-cape), Visibility (nwm-vis), Cloud Cover
  // (nwm-tcdc), and Lightning Forecast (nwm-ltng) entries were RETIRED in the weather
  // reorg — each has a worldwide OM equivalent that also covers the US (auto-selecting
  // HRRR there). They are intentionally absent from this registry so stale saved
  // state for them is pruned on load and they can never resurface.
  [
    'nwm-q2d',
    {
      tier: LayerTier.Atmospheric,
      order: 50,
      name: 'Humidity',
      kind: 'nwm',
      tags: ['humidity', 'moisture', 'nwm', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-swdown',
    {
      tier: LayerTier.Atmospheric,
      order: 60,
      name: 'Solar Radiation',
      kind: 'nwm',
      tags: ['solar', 'radiation', 'sun', 'nwm', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'lightning-unified',
    {
      tier: LayerTier.Atmospheric,
      order: 75,
      name: 'Lightning',
      kind: 'lightning',
      tags: ['lightning', 'thunderstorm', 'flash', 'storm', 'worldwide', 'lpi', 'wmo'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-goes_glm',
    {
      tier: LayerTier.Atmospheric,
      order: 90,
      name: 'Lightning (GLM)',
      kind: 'nwm',
      tags: ['lightning', 'observed', 'goes', 'glm', 'flash'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-goes_cloud',
    {
      tier: LayerTier.Atmospheric,
      order: 110,
      name: 'Cloud Cover (GOES)',
      kind: 'nwm',
      tags: ['cloud', 'cover', 'observed', 'goes', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-refc',
    {
      tier: LayerTier.Atmospheric,
      order: 130,
      name: 'Reflectivity',
      kind: 'nwm',
      tags: ['reflectivity', 'radar', 'composite', 'hrrr', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'nwm-mrms_radar',
    {
      tier: LayerTier.Atmospheric,
      order: 150,
      name: 'Radar',
      kind: 'nwm',
      tags: ['radar', 'mrms', 'nexrad', 'observed', 'precipitation'],
      browseGroup: 'weather',
    },
  ],

  // ── Tier 3: Atmospheric — Open-Meteo Worldwide ──────────────────
  [
    'subgroup-cloud-forecast',
    {
      tier: LayerTier.Atmospheric,
      order: 200,
      name: 'Cloud Forecast',
      kind: 'subgroup',
      tags: ['cloud', 'cover', 'forecast', 'high', 'mid', 'low', 'open-meteo', 'weather'],
      browseGroup: 'weather',
      children: [
        'om-cloud-cover',
        'om-cloud-cover-high',
        'om-cloud-cover-mid',
        'om-cloud-cover-low',
      ],
    },
  ],
  [
    'om-cloud-cover',
    {
      tier: LayerTier.Atmospheric,
      order: 200,
      name: 'Cloud Cover',
      kind: 'om',
      tags: ['cloud', 'cover', 'worldwide', 'open-meteo', 'weather'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-high',
    {
      tier: LayerTier.Atmospheric,
      order: 210,
      name: 'Cloud Cover High',
      kind: 'om',
      tags: ['cloud', 'cover', 'high', 'cirrus', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-mid',
    {
      tier: LayerTier.Atmospheric,
      order: 220,
      name: 'Cloud Cover Mid',
      kind: 'om',
      tags: ['cloud', 'cover', 'mid', 'alto', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cloud-cover-low',
    {
      tier: LayerTier.Atmospheric,
      order: 230,
      name: 'Cloud Cover Low',
      kind: 'om',
      tags: ['cloud', 'cover', 'low', 'stratus', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-precipitation',
    {
      tier: LayerTier.Atmospheric,
      order: 250,
      name: 'Precipitation',
      kind: 'om',
      tags: ['precipitation', 'rain', 'snow', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-cape',
    {
      tier: LayerTier.Atmospheric,
      order: 260,
      name: 'CAPE',
      kind: 'om',
      tags: ['cape', 'convective', 'storm', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-visibility',
    {
      tier: LayerTier.Atmospheric,
      order: 270,
      name: 'Visibility',
      kind: 'om',
      tags: ['visibility', 'fog', 'haze', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-wind',
    {
      tier: LayerTier.Atmospheric,
      order: 280,
      name: 'Wind',
      kind: 'om',
      tags: ['wind', 'speed', 'gust', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-temperature',
    {
      tier: LayerTier.Atmospheric,
      order: 300,
      name: 'Temperature',
      kind: 'om',
      tags: ['temperature', 'temp', 'worldwide', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-smoke',
    {
      tier: LayerTier.Atmospheric,
      order: 310,
      name: 'Smoke',
      kind: 'om',
      tags: ['smoke', 'fire', 'air', 'quality', 'open-meteo'],
      browseGroup: 'hazards',
    },
  ],
  [
    'om-freezing-level',
    {
      tier: LayerTier.Atmospheric,
      order: 320,
      name: 'Freezing Level',
      kind: 'om',
      tags: ['freezing', 'level', 'height', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],
  [
    'om-snow-line',
    {
      tier: LayerTier.Atmospheric,
      order: 330,
      name: 'Snow Line',
      kind: 'om',
      tags: ['snow', 'line', 'snowfall', 'height', 'open-meteo'],
      browseGroup: 'weather',
    },
  ],

  // ── Tier 4: Reference ────────────────────────────────────────────
  [
    'graticule',
    {
      tier: LayerTier.Reference,
      order: 10,
      name: 'Lat/Lon Graticule',
      kind: 'terrain-service',
      tags: ['graticule', 'latitude', 'longitude', 'grid', 'coordinate', 'degree'],
      browseGroup: 'reference',
    },
  ],
  [
    'utm-grid',
    {
      tier: LayerTier.Reference,
      order: 20,
      name: 'UTM Grid',
      kind: 'terrain-service',
      tags: ['utm', 'grid', 'zone', 'universal', 'transverse', 'mercator', 'coordinate'],
      browseGroup: 'reference',
    },
  ],
  [
    'mgrs-grid',
    {
      tier: LayerTier.Reference,
      order: 30,
      name: 'MGRS Grid',
      kind: 'terrain-service',
      tags: ['mgrs', 'military', 'grid', 'reference', 'coordinate', 'nato'],
      browseGroup: 'reference',
    },
  ],
  [
    'declination',
    {
      tier: LayerTier.Reference,
      order: 40,
      name: 'Magnetic Declination',
      kind: 'terrain-service',
      tags: ['declination', 'magnetic', 'north', 'compass', 'isogonic', 'wmm', 'navigation'],
      browseGroup: 'reference',
    },
  ],
  [
    'range-rings',
    {
      tier: LayerTier.Reference,
      order: 50,
      name: 'Range Rings',
      kind: 'terrain-service',
      tags: ['range', 'rings', 'distance', 'radius', 'sar', 'search', 'rescue'],
      browseGroup: 'reference',
    },
  ],
]

// ── Tag metadata ───────────────────────────────────────────────────
// Kept as compact id→value maps (rather than inline on each entry) so the taxonomy is
// reviewable in one place and the entries above stay terse. Merged into the entries below.

// Most-specific region each layer has useful data for. Anything not listed is treated as
// worldwide and shows no region chip (we dropped the "(Worldwide)" name suffix in favour
// of this). Use the tightest accurate scope — e.g. COTREX is Colorado-only, not just US.
const LAYER_REGION: Record<string, string> = {
  // US-scoped (CONUS data products / US agencies)
  'snow-depth': 'US',
  'nwm-snowh': 'US',
  'nwm-qsnow': 'US',
  'avalanche-zones': 'US',
  cellular: 'US',
  'blm-sma': 'US',
  padus: 'US',
  plss: 'US',
  'fire-wildfires': 'US',
  'fire-restrictions': 'US',
  'emergency-hospitals': 'US',
  'emergency-fire-stations': 'US',
  'emergency-law-enforcement': 'US',
  'emergency-usfs-offices': 'US',
  'wildlife-critical-habitat': 'US',
  'wildlife-nps-alerts': 'US',
  'nwm-q2d': 'US',
  'nwm-swdown': 'US',
  'nwm-refc': 'US',
  'nwm-mrms_radar': 'US',
  'nwm-goes_glm': 'US',
  'nwm-goes_cloud': 'US',
  // State-scoped
  cotrex: 'Colorado',
  'cotrex-closures': 'Colorado',
}

// Whether a layer is a model prediction vs a measurement. Only set where the distinction
// is meaningful and non-obvious (weather + observational data); static/reference/terrain
// and plain imagery are left unset.
const LAYER_TIMELINESS: Record<string, 'forecast' | 'observed'> = {
  // Forecast / model output
  'nwm-snowh': 'forecast',
  'nwm-qsnow': 'forecast',
  'nwm-q2d': 'forecast',
  'nwm-swdown': 'forecast',
  'nwm-refc': 'forecast',
  'avalanche-zones': 'forecast',
  'lightning-unified': 'forecast',
  'om-cloud-cover': 'forecast',
  'om-cloud-cover-high': 'forecast',
  'om-cloud-cover-mid': 'forecast',
  'om-cloud-cover-low': 'forecast',
  'om-precipitation': 'forecast',
  'om-cape': 'forecast',
  'om-visibility': 'forecast',
  'om-wind': 'forecast',
  'om-temperature': 'forecast',
  'om-smoke': 'forecast',
  'om-freezing-level': 'forecast',
  'om-snow-line': 'forecast',
  // Observed / measured
  'snow-depth': 'observed',
  'nwm-mrms_radar': 'observed',
  'nwm-goes_glm': 'observed',
  'nwm-goes_cloud': 'observed',
  'viirs-snpp-snow-wms': 'observed',
  'viirs-noaa20-snow-wms': 'observed',
  'fire-firms': 'observed',
  'fire-wildfires': 'observed',
}

for (const [id, entry] of entries) {
  const region = LAYER_REGION[id]
  if (region) entry.region = region
  const timeliness = LAYER_TIMELINESS[id]
  if (timeliness) entry.timeliness = timeliness
}

export const LAYER_TIER_MAP: ReadonlyMap<string, LayerTierEntry> = new Map(entries)

export const TIER_LABELS: Record<LayerTier, string> = {
  [LayerTier.Imagery]: 'Imagery',
  [LayerTier.GroundFixed]: 'Ground',
  [LayerTier.Data]: 'Data',
  [LayerTier.Atmospheric]: 'Atmospheric',
  [LayerTier.Reference]: 'Reference',
}

export function getTier(layerId: string): LayerTier | undefined {
  return LAYER_TIER_MAP.get(layerId)?.tier
}

export function getTierEntry(layerId: string): LayerTierEntry | undefined {
  return LAYER_TIER_MAP.get(layerId)
}

export function getDefaultOrder(layerId: string): number {
  return LAYER_TIER_MAP.get(layerId)?.order ?? 1000
}

export function searchLayers(query: string): LayerTierEntry[] {
  if (!query) return []
  const lower = query.toLowerCase()
  const terms = lower.split(/\s+/).filter(t => t.length > 1)
  const results: LayerTierEntry[] = []
  for (const entry of LAYER_TIER_MAP.values()) {
    const nameMatch = terms.every(
      t => entry.name.toLowerCase().includes(t) || entry.tags.some(tag => tag.includes(t))
    )
    if (nameMatch) results.push(entry)
  }
  return results
}
